import React from "react";
import { useTabParam } from "../hooks/useTabParam";
import {
  fetchProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  fetchProductSummary,
  fetchProductTransactions,
} from "../services/ProductsNewService";
import { Box, Button, CircularProgress, IconButton, MenuItem, Select, Tab, Tabs, Typography } from "@mui/material";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import { COLORS } from "../assets/theme";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowBackIosNew from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CustomTable from "../components/table/CustomTable";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import { toast } from "react-toastify";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomDateField from "../components/CustomDateField";
import CustomSelect from "../components/CustomSelect";
import CustomTextField from "../components/CustomTextField";
import { getAllBusinesses } from "../services/VenueService";


export type ProductRow = {
  id: string;
  name: string;
  packageType: "UNIT" | "PACK";
  sale_price: number | null;
  cost_price: number | null;
  created_at: string;
}

const downloadXlsx = (sheetName: string, sheetRows: any[][], fileName: string) => {
  const escapeXml = (value: any) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  const columnName = (index: number) => {
    let name = "";
    let current = index + 1;
    while (current > 0) {
      const remainder = (current - 1) % 26;
      name = String.fromCharCode(65 + remainder) + name;
      current = Math.floor((current - 1) / 26);
    }
    return name;
  };
  const maxColumns = Math.max(...sheetRows.map((row) => row.length), 1);
  const worksheetRows = sheetRows
    .map((row, rowIndex) => {
      const cells = row
        .map((cell, colIndex) => {
          const style = rowIndex === 0 ? ' s="1"' : "";
          return `<c r="${columnName(colIndex)}${rowIndex + 1}" t="inlineStr"${style}><is><t>${escapeXml(
            cell
          )}</t></is></c>`;
        })
        .join("");
      return `<row r="${rowIndex + 1}">${cells}</row>`;
    })
    .join("");
  const worksheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><cols>${Array.from(
    { length: maxColumns },
    (_, idx) => `<col min="${idx + 1}" max="${idx + 1}" width="18" customWidth="1"/>`
  ).join("")}</cols><sheetData>${worksheetRows}</sheetData></worksheet>`;
  const safeSheetName =
    sheetName.replace(new RegExp(String.raw`[[\]*?:/\\]`, "g"), " ").slice(0, 31) ||
    "Sheet1";
  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?><workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${escapeXml(
    safeSheetName
  )}" sheetId="1" r:id="rId1"/></sheets></workbook>`;
  const workbookRelsXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>';
  const relsXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>';
  const stylesXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="1"><fill><patternFill patternType="none"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs></styleSheet>';
  const contentTypesXml =
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?><Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>';
  const encoder = new TextEncoder();
  const crcTable = Array.from({ length: 256 }, (_, n) => {
    let c = n;
    for (let k = 0; k < 8; k += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    return c >>> 0;
  });
  const crc32 = (data: Uint8Array) => {
    let crc = 0xffffffff;
    data.forEach((byte) => {
      crc = crcTable[(crc ^ byte) & 0xff] ^ (crc >>> 8);
    });
    return (crc ^ 0xffffffff) >>> 0;
  };
  const u16 = (value: number) => [value & 0xff, (value >>> 8) & 0xff];
  const u32 = (value: number) => [
    value & 0xff,
    (value >>> 8) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 24) & 0xff,
  ];
  const files = [
    { name: "[Content_Types].xml", content: contentTypesXml },
    { name: "_rels/.rels", content: relsXml },
    { name: "xl/workbook.xml", content: workbookXml },
    { name: "xl/_rels/workbook.xml.rels", content: workbookRelsXml },
    { name: "xl/styles.xml", content: stylesXml },
    { name: "xl/worksheets/sheet1.xml", content: worksheetXml },
  ];
  const localParts: Uint8Array[] = [];
  const centralParts: Uint8Array[] = [];
  let offset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const crc = crc32(data);
    const localHeader = new Uint8Array([
      ...u32(0x04034b50),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(nameBytes.length),
      ...u16(0),
    ]);
    localParts.push(localHeader, nameBytes, data);

    const centralHeader = new Uint8Array([
      ...u32(0x02014b50),
      ...u16(20),
      ...u16(20),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(crc),
      ...u32(data.length),
      ...u32(data.length),
      ...u16(nameBytes.length),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u16(0),
      ...u32(0),
      ...u32(offset),
    ]);
    centralParts.push(centralHeader, nameBytes);
    offset += localHeader.length + nameBytes.length + data.length;
  });

  const centralSize = centralParts.reduce((sum, part) => sum + part.length, 0);
  const endRecord = new Uint8Array([
    ...u32(0x06054b50),
    ...u16(0),
    ...u16(0),
    ...u16(files.length),
    ...u16(files.length),
    ...u32(centralSize),
    ...u32(offset),
    ...u16(0),
  ]);
  const blobParts: BlobPart[] = [...localParts, ...centralParts, endRecord].map(
    (part) => part as BlobPart
  );
  const blob = new Blob(blobParts, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const Products: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const [rows, setRows] = React.useState<ProductRow[]>([]);
  const {
    tab: tabIndex,
    setTab: setTabIndex,
    panelRef: tabPanelRef,
  } = useTabParam(["list", "summary", "transactions"]);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<ProductRow | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [toDeleteId, setToDeleteId] = React.useState<string | null>(null);
  const [reportsLoading, setReportsLoading] = React.useState(false);
  const [reportsError, setReportsError] = React.useState<string | null>(null);
  const [summaryResponse, setSummaryResponse] = React.useState<any>(null);
  const [transactionsResponse, setTransactionsResponse] = React.useState<any>(null);
  const [summaryFrom, setSummaryFrom] = React.useState<string | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [summaryTo, setSummaryTo] = React.useState<string | null>(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [summaryPage, setSummaryPage] = React.useState(1);
  const [summarySize, setSummarySize] = React.useState(10);
  const [businesses, setBusinesses] = React.useState<any[]>([]);
  const [selectedBusinessId, setSelectedBusinessId] = React.useState<string>("all");
  const [summarySearch, setSummarySearch] = React.useState("");
  const [transactionsBusinessId, setTransactionsBusinessId] = React.useState<string>("all");
  const [transactionsFrom, setTransactionsFrom] = React.useState<string | null>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [transactionsTo, setTransactionsTo] = React.useState<string | null>(() => {
    return new Date().toISOString().slice(0, 10);
  });
  const [transactionsPage, setTransactionsPage] = React.useState(1);
  const [transactionsSize, setTransactionsSize] = React.useState(10);
  const [transactionsSearch, setTransactionsSearch] = React.useState("");
  
  React.useEffect(()=> {
    let mounted = true;
    const loadProducts = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProducts() as ProductRow[];
        if (mounted) {
          setRows(data.map(mapProductRecord));
        }
      } catch (error: any) {
        if (mounted) setError(error?.response?.data?.message || error?.message || "Failed to load products.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProducts();
    return () => {
      mounted = false;
    };
  },[]);

  React.useEffect(() => {
    let mounted = true;
    const loadBusinesses = async () => {
      try {
        const data = await getAllBusinesses();
        if (mounted) setBusinesses(Array.isArray(data) ? data : []);
      } catch (err) {
        if (mounted) setBusinesses([]);
      }
    };
    loadBusinesses();
    return () => {
      mounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (tabIndex !== 1) return;
    let mounted = true;
    const fetchSummary = async () => {
      setReportsLoading(true);
      setReportsError(null);
      try {
        const summary = await fetchProductSummary({
          businessId: selectedBusinessId !== "all" ? selectedBusinessId : undefined,
          from: summaryFrom || undefined,
          to: summaryTo || undefined,
        });
        if (mounted) setSummaryResponse(summary);
      } catch (err: any) {
        if (mounted) {
          setReportsError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load summary."
          );
        }
      } finally {
        if (mounted) setReportsLoading(false);
      }
    };

    fetchSummary();
    return () => {
      mounted = false;
    };
  }, [tabIndex, selectedBusinessId, summaryFrom, summaryTo]);

  React.useEffect(() => {
    if (tabIndex !== 2) return;
    let mounted = true;
    const fetchTransactions = async () => {
      setReportsLoading(true);
      setReportsError(null);
      try {
        const transactions = await fetchProductTransactions({
          businessId: transactionsBusinessId !== "all" ? transactionsBusinessId : undefined,
          from: transactionsFrom || undefined,
          to: transactionsTo || undefined,
          page: transactionsPage - 1,
          size: transactionsSize,
        });
        if (mounted) setTransactionsResponse(transactions);
      } catch (err: any) {
        if (mounted) {
          setReportsError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load transactions."
          );
        }
      } finally {
        if (mounted) setReportsLoading(false);
      }
    };

    fetchTransactions();
    return () => {
      mounted = false;
    };
  }, [tabIndex, transactionsBusinessId, transactionsFrom, transactionsTo, transactionsPage, transactionsSize]);
  
  const mapProductRecord = (item: any): ProductRow => {
    const costPrice = item?.cost_price ?? item?.costPrice ?? null;
    const salePrice = item?.sale_price ?? item?.salePrice ?? null;
    return {
      id: String(item?.id),
      name: String(item?.name ?? ""),
      packageType: item?.package_type ?? item?.packageType ?? "UNIT",
      cost_price: costPrice !== null && costPrice !== undefined ? Number(costPrice) : null,
      sale_price: salePrice !== null && salePrice !== undefined ? Number(salePrice) : null,
      created_at: String(item?.created_at ?? item?.createdAt ?? ""),
    };
  }
const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.msg ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any, opts: any = {}) =>
  toast.error(String(message), { toastId: String(message), ...opts });

const formatDateDMY = (value?: string | null) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.getDate()}-${d.getMonth() + 1}-${d.getFullYear()}`;
};

const formatNumber2 = (value: any) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : String(value);
};

  const openEditForId = (id: string | number) => {
    const found = rows.find((r) => String(r.id) === String(id));
    if (found) {
      setEditingRow(found);
      setEditOpen(true);
    }
  };

  const wiredActions = Actions({
    handleOpenEditModal: { handler: openEditForId },
    onDelete: {
      handler: (id: number | string) => {
        setToDeleteId(String(id));
        setConfirmOpen(true);
      },
    },
  });

  const exportCsv = React.useCallback(() => {
    if (!rows || rows.length === 0) return;
    const keys = [
      "name",
      "packageType",
      "cost_price",
      "sale_price",
      "created_at",
    ];
    const header = keys.join(",");
    const lines = rows.map((r) => {
      const row: Record<string, any> = {
        name: r.name ?? "-",
        packageType: r.packageType ?? "-",
        cost_price: r.cost_price ?? "-",
        sale_price: r.sale_price ?? "-",
        created_at: r.created_at ?? "-",
      };
      return keys
        .map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`)
        .join(",");
    });
    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `products-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  const exportSummaryXlsx = React.useCallback(() => {
    const items = Array.isArray(summaryResponse?.items)
      ? summaryResponse.items
      : Array.isArray(summaryResponse)
      ? summaryResponse
      : [];
    const searchTerm = summarySearch.trim().toLowerCase();
    const exportItems = searchTerm
      ? items.filter((i: any) =>
          String(i?.productName || "").toLowerCase().includes(searchTerm)
        )
      : items;

    if (exportItems.length === 0) {
      toast.info("No summary data to export.");
      return;
    }

    const numberOrDash = (value: any) => {
      if (value === null || value === undefined || value === "") return "-";
      const numberValue = Number(value);
      return Number.isFinite(numberValue) ? numberValue.toFixed(2) : String(value);
    };
    const total = (key: string) =>
      exportItems.reduce((sum: number, item: any) => sum + (Number(item?.[key]) || 0), 0);
    const selectedBusiness = businesses.find(
      (business) => String(business?.id) === selectedBusinessId
    );

    const sheetRows = [
      [
        "Product",
        "Package Type",
        "Cost Price",
        "Sale Price",
        "Bought Qty",
        "Bought Total",
        "Sold Qty",
        "Sold Total",
        "Cost Total",
        "Profit",
      ],
      ...exportItems.map((item: any) => [
        item?.productName || "Product",
        item?.packageType || "-",
        numberOrDash(item?.costPrice),
        numberOrDash(item?.salePrice),
        numberOrDash(item?.boughtQty),
        numberOrDash(item?.boughtTotal),
        numberOrDash(item?.soldQty),
        numberOrDash(item?.soldTotal),
        numberOrDash(item?.costTotal),
        numberOrDash(item?.profit),
      ]),
      [],
      ["Filters"],
      ["Business", selectedBusinessId === "all" ? "All" : selectedBusiness?.name || selectedBusinessId],
      ["From", summaryFrom || "-"],
      ["To", summaryTo || "-"],
      ...(summarySearch.trim() ? [["Search", summarySearch.trim()]] : []),
      [],
      ["Totals"],
      ["Total Items", exportItems.length],
      ["Total Bought Qty", total("boughtQty").toFixed(2)],
      ["Total Bought Amount", total("boughtTotal").toFixed(2)],
      ["Total Sold Qty", total("soldQty").toFixed(2)],
      ["Total Sold Amount", total("soldTotal").toFixed(2)],
      ["Total Cost", total("costTotal").toFixed(2)],
      ["Total Profit", total("profit").toFixed(2)],
    ];

    downloadXlsx(
      "Product Summary",
      sheetRows,
      `product-summary-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  }, [
    businesses,
    selectedBusinessId,
    summaryFrom,
    summaryResponse,
    summarySearch,
    summaryTo,
  ]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => {
      const name = String(r.name || "").toLowerCase();
      const pkg = String(r.packageType || "").toLowerCase();
      const cost = r.cost_price !== null && r.cost_price !== undefined
        ? String(r.cost_price).toLowerCase()
        : "";
      const sale = r.sale_price !== null && r.sale_price !== undefined
        ? String(r.sale_price).toLowerCase()
        : "";
      return (
        name.includes(term) ||
        pkg.includes(term) ||
        cost.includes(term) ||
        sale.includes(term)
      );
    });
  }, [rows, search]);
  
    const formFields: Field[] =[
      {name: "name", label: "Name", type: "text", required: true},
      {name: "packageType", label: "Package Type", type: "dropdown", options: [
        {label: "Unit", value: "UNIT"},
        {label: "Pack", value: "PACK"},
      ], required: true, disabled: true},
      {name: "cost_price", label: "Cost Price", type: "number"},
      {name: "sale_price", label: "Sale Price", type: "number"},
    ]

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader
        title="Products"
        subtitle="Your product catalogue, stock summary and transactions."
      />
      <Box
        sx={{
          bgcolor: COLORS.WhiteBackground,
          borderRadius: 2,
          boxShadow: 3,
          position: "relative",
          minHeight: "80vh",
        }}
      >
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            label="List Products"
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              color: (t) => t.palette.text.secondary,
              "&:hover": { bgcolor: "transparent" },
            }}
          />
          <Tab
            label="Summary"
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              color: (t) => t.palette.text.secondary,
              "&:hover": { bgcolor: "transparent" },
            }}
          />
          <Tab
            label="Product Transactions"
            sx={{
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              color: (t) => t.palette.text.secondary,
              "&:hover": { bgcolor: "transparent" },
            }}
          />
        </Tabs>
        <Box p={2} ref={tabPanelRef}>
          {tabIndex === 0 && (
            <>
              <SearchHeaderContainer
                search={search}
                setSearch={setSearch}
                buttonConfig={[
                  {
                    size: "small",
                    bgColor: COLORS.MainColor,
                    startIcon: <AddIcon />,
                    onClick: () => {
                      setEditingRow(null);
                      setEditOpen(true);
                    },
                    textColor: COLORS.WhiteText,
                  },
                  {
                    label: "Export CSV",
                    onClick: () => Promise.resolve(exportCsv()),
                    startIcon: <FileDownloadIcon />,
                    variant: "contained",
                    bgColor: COLORS.MainColor,
                    textColor: "#fff",
                    showExportProgress: true,
                  },
                ]}
              />

              {error ? (
                <Typography color="error" sx={{ px: 2, pb: 1 }}>
                  {error}
                </Typography>
              ) : null}

              <Box sx={{ width: "98%", mx: "auto" }}>
                <CustomTable<ProductRow>
                  filters={{ page: 0, size: 10, search }}
                  clientSidePagination
                  loading={loading}
                  fields={[
                    { key: "name", header: "Name" },
                    { key: "packageType", header: "Package Type" },
                    {
                      key: "cost_price",
                      header: "Cost Price",
                      transform: (r) =>
                        r.cost_price !== null && r.cost_price !== undefined
                          ? r.cost_price
                          : "-",
                    },
                    {
                      key: "sale_price",
                      header: "Sale Price",
                      transform: (r) =>
                        r.sale_price !== null && r.sale_price !== undefined
                          ? r.sale_price
                          : "-",
                    },
                    {
                      key: "created_at",
                      header: "Created At",
                      transform: (r) =>
                        r.created_at
                          ? new Date(r.created_at).toLocaleDateString()
                          : "-",
                    },
                  ]}
                  fetchFunc={async () => ({
                    content: filteredRows,
                    number: 0,
                    size: filteredRows.length,
                    totalElements: filteredRows.length,
                    totalPages: 1,
                  })}
                  actions={wiredActions}
                />
              </Box>

              <GenericFormDialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                title={editingRow ? "Edit Product" : "Add Product"}
                fields={formFields}
                initialValues={
                  editingRow
                    ? {
                        name: editingRow.name,
                        packageType: editingRow.packageType,
                        cost_price: editingRow.cost_price ?? undefined,
                        sale_price: editingRow.sale_price ?? undefined,
                      }
                    : {
                        packageType: "UNIT",
                      }
                }
                onSubmit={async (values) => {
                  try {
                    const toPrice = (value: any) => {
                      if (value === undefined || value === null) return null;
                      const raw = String(value).trim();
                      if (raw === "") return null;
                      const parsed = Number(raw);
                      return Number.isFinite(parsed) ? parsed : null;
                    };
                    const payload = {
                      name: values.name,
                      packageType: values.packageType,
                      costPrice: toPrice(values.cost_price),
                      salePrice: toPrice(values.sale_price),
                    };
                    if (editingRow) {
                      await updateProduct(editingRow.id, payload);
                      setRows((prev) =>
                        prev.map((r) =>
                          r.id === editingRow.id ? { ...r, ...payload } : r
                        )
                      );
                    } else {
                      const newProduct = await createProduct(payload);
                      setRows((prev) => [
                        { ...mapProductRecord(newProduct) },
                        ...prev,
                      ]);
                    }
                    setEditOpen(false);
                  } catch (error) {
                    showErrorToast(
                      getErrorMessage(error, "Failed to save banner.")
                    );
                  }
                }}
              />

              <ConfirmDialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Delete Product"
                message="Are you sure you want to delete this product?"
                onConfirm={async () => {
                  if (!toDeleteId) return;
                  try {
                    await deleteProduct(toDeleteId);
                    setRows((prev) =>
                      prev.filter((r) => String(r.id) !== String(toDeleteId))
                    );
                    toast.success("Product deleted successfully!");
                  } catch (error: any) {
                    showErrorToast(
                      getErrorMessage(error, "Failed to delete product.")
                    );
                  } finally {
                    setConfirmOpen(false);
                    setToDeleteId(null);
                  }
                }}
              />
            </>
          )}

          {tabIndex === 1 && (
            <Box sx={{ display: "grid", gap: 2 }}>
              {(() => {
                const items = Array.isArray(summaryResponse?.items)
                  ? summaryResponse.items
                  : Array.isArray(summaryResponse)
                  ? summaryResponse
                  : [];
                const searchTerm = summarySearch.trim().toLowerCase();
                const filteredItems = searchTerm
                  ? items.filter((i: any) =>
                      String(i?.productName || "").toLowerCase().includes(searchTerm)
                    )
                  : items;
                const totalItems = filteredItems.length;
                const totalSoldQty = items.reduce(
                  (sum: number, i: any) => sum + (Number(i?.soldQty) || 0),
                  0
                );
                const totalSoldAmount = items.reduce(
                  (sum: number, i: any) => sum + (Number(i?.soldTotal) || 0),
                  0
                );
                const totalCost = items.reduce(
                  (sum: number, i: any) => sum + (Number(i?.costTotal) || 0),
                  0
                );
                const totalProfit = items.reduce(
                  (sum: number, i: any) => sum + (Number(i?.profit) || 0),
                  0
                );
                const totalBoughtQty = items.reduce(
                  (sum: number, i: any) => sum + (Number(i?.boughtQty) || 0),
                  0
                );
                const totalBoughtAmount = items.reduce(
                  (sum: number, i: any) => sum + (Number(i?.boughtTotal) || 0),
                  0
                );
                const totalPages = Math.max(1, Math.ceil(totalItems / summarySize));
                const startIndex = (summaryPage - 1) * summarySize;
                const pagedItems = filteredItems.slice(startIndex, startIndex + summarySize);

                return (
                  <>
                    <Box display="flex" gap={3}>
                      <Box flex={2}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={2}
                          flexWrap="wrap"
                          mb={1}
                        >
                          {summaryResponse?.organizationName || summaryResponse?.scopeName ? (
                            <Typography sx={{ fontWeight: 600 }}>
                              {summaryResponse?.organizationName || summaryResponse?.scopeName}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontWeight: 600 }}>Summary</Typography>
                          )}

                          <Box
                            sx={{ width: { xs: "100%", sm: 160, md: 180 }, maxWidth: 180 }}
                          >
                            <CustomSelect
                              options={[
                                { label: "All", value: "all" },
                                ...businesses.map((b) => ({
                                  label: b.name,
                                  value: String(b.id),
                                })),
                              ]}
                              value={selectedBusinessId}
                              onChange={(v: any) => {
                                setSelectedBusinessId(String(v));
                                setSummaryPage(1);
                              }}
                              placeholder="Business"
                              size="medium"
                              searchable={false}
                              multiSelect={false}
                              sx={{
                                width:'180px',
                                backgroundColor: COLORS.WhiteBackground,
                                "& .MuiSelect-select": {
                                  padding: "8px 12px",
                                },
                              }}
                            />
                          </Box>

                          <Box sx={{ width: { xs: "100%", sm: 180 }, maxWidth: 200 }}>
                            <CustomTextField
                              size="small"
                              startIcon={<SearchIcon />}
                              placeholder="Search product"
                              bgColor={COLORS.WhiteBackground}
                              value={summarySearch}
                              onChange={(e) => {
                                setSummarySearch(e.target.value);
                                setSummaryPage(1);
                              }}
                              sx={{ width: "100%" }}
                            />
                          </Box>

                          <Box
                            sx={{
                              ml: { xs: 0, md: "auto" },
                              width: { xs: "100%", md: "auto" },
                              display: "flex",
                              justifyContent: { xs: "flex-start", md: "flex-end" },
                            }}
                          >
                            <Button
                              variant="contained"
                              size="small"
                              startIcon={<FileDownloadIcon />}
                              onClick={exportSummaryXlsx}
                              disabled={reportsLoading || filteredItems.length === 0}
                              sx={{
                                backgroundColor: COLORS.MainColor,
                                color: "#fff",
                                textTransform: "none",
                                height: 36,
                                "&:hover": { backgroundColor: COLORS.MainColor },
                              }}
                            >
                              Export XLSX
                            </Button>
                          </Box>
                        </Box>
                        <Box
                          mb={1}
                          display="flex"
                          gap={1}
                          flexWrap="wrap"
                          alignItems="flex-end"
                        >
                          <Box minWidth={90}>
                            <CustomLabel text="From" variant="body1" gutterBottom />
                            <CustomDateField
                              sx={{ width: "120px" }}
                              value={summaryFrom || ""}
                              onChange={(value) => {
                                setSummaryFrom(value || null);
                                setSummaryPage(1);
                              }}
                              fullWidth
                            />
                          </Box>

                          <Box minWidth={90}>
                            <CustomLabel text="To" variant="body1" gutterBottom />
                            <CustomDateField
                              sx={{ width: "120px" }}
                              value={summaryTo || ""}
                              onChange={(value) => {
                                setSummaryTo(value || null);
                                setSummaryPage(1);
                              }}
                              fullWidth
                            />
                          </Box>

                          <Box display="flex" alignItems="flex-end" gap={1} mb={0.5}>
                            <Typography
                              sx={{ fontSize: "0.875rem", color: "#9ca3af", mb: "6px" }}
                            >
                              Rows per page:
                            </Typography>
                            <Select
                              value={summarySize}
                              onChange={(e) => {
                                setSummarySize(Number(e.target.value));
                                setSummaryPage(1);
                              }}
                              sx={{ height: "32px", fontSize: "0.875rem", width: "80px" }}
                            >
                              <MenuItem value={10}>10</MenuItem>
                              <MenuItem value={20}>20</MenuItem>
                              <MenuItem value={50}>50</MenuItem>
                              <MenuItem value={100}>100</MenuItem>
                            </Select>
                          </Box>

                          <Box display="flex" alignItems="flex-end" gap={0.5} mb={0.5}>
                            <IconButton
                              disabled={summaryPage <= 1}
                              onClick={() =>
                                setSummaryPage((p) => Math.max(p - 1, 1))
                              }
                              sx={{ color: summaryPage > 1 ? "#9ca3af" : "#ccc", p: 0.5 }}
                            >
                              <ArrowBackIosNew fontSize="small" />
                            </IconButton>

                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#9ca3af",
                                alignSelf: "center",
                              }}
                            >
                              {summaryPage} / {totalPages}
                            </Typography>

                            <IconButton
                              disabled={summaryPage >= totalPages}
                              onClick={() =>
                                setSummaryPage((p) => Math.min(p + 1, totalPages))
                              }
                              sx={{
                                color: summaryPage < totalPages ? "#9ca3af" : "#ccc",
                                p: 0.5,
                              }}
                            >
                              <ArrowForwardIos fontSize="small" />
                            </IconButton>
                          </Box>

                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Typography sx={{ fontSize: "0.875rem", color: "#9ca3af" }}>
                              Total Items: {totalItems}
                            </Typography>
                          </Box>
                        </Box>

                        {reportsError ? (
                          <Typography color="error">{reportsError}</Typography>
                        ) : null}

                        {reportsLoading ? (
                          <Box display="flex" justifyContent="center" py={2}>
                            <CircularProgress />
                          </Box>
                        ) : pagedItems.length === 0 ? (
                          <CustomLabel text="No summary items found." />
                        ) : (
                          pagedItems.map((item: any, idx: number) => {
                            const profitValue = Number(item?.profit) || 0;
                            const profitColor =
                              profitValue < 0 ? "#ef4444" : "#16a34a";
                            return (
                              <Box
                                key={item?.productId || idx}
                                sx={{
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 2,
                                  p: 2,
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 1.5,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "1fr",
                                      md: "240px 1fr",
                                    },
                                    columnGap: 2,
                                    rowGap: 1,
                                    alignItems: "start",
                                  }}
                                >
                                  <Box sx={{ gridColumn: "1 / 2", gridRow: "1" }}>
                                    <CustomLabel
                                      text={`${item?.productName || "Product"}${
                                        item?.packageType ? ` • ${item.packageType}` : ""
                                      }`}
                                      fontWeight={700}
                                      fontSize={16}
                                    />
                                  </Box>
                                  <Box sx={{ gridColumn: "2 / 3", gridRow: "1" }} />
                                  <Box
                                    sx={{
                                      gridColumn: "1 / 2",
                                      gridRow: "2",
                                      display: "grid",
                                      gridTemplateColumns: "1fr",
                                      rowGap: 0.5,
                                      mt: 0.5,
                                    }}
                                  >
                                    <CustomLabel
                                      text={`Cost Price: ${
                                        item?.costPrice ?? item?.costPrice === 0
                                          ? formatNumber2(item.costPrice)
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                    <CustomLabel
                                      text={`Sale Price: ${
                                        item?.salePrice ?? item?.salePrice === 0
                                          ? formatNumber2(item.salePrice)
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                  </Box>
                                  <Box
                                    sx={{
                                      gridColumn: "2 / 3",
                                      gridRow: "2",
                                      display: "grid",
                                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                      columnGap: 4,
                                      rowGap: 0.5,
                                    }}
                                  >
                                    <CustomLabel
                                      text={`Bought Qty: ${
                                        item?.boughtQty ?? item?.boughtQty === 0
                                          ? formatNumber2(item.boughtQty)
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                    <CustomLabel
                                      text={`Sold Qty: ${
                                        item?.soldQty ?? item?.soldQty === 0
                                          ? formatNumber2(item.soldQty)
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                    <CustomLabel
                                      text={`Cost Total: ${
                                        item?.costTotal ?? item?.costTotal === 0
                                          ? formatNumber2(item.costTotal)
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                    <CustomLabel
                                      text={`Sold Total: ${
                                        item?.soldTotal ?? item?.soldTotal === 0
                                          ? formatNumber2(item.soldTotal)
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                  </Box>
                                </Box>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: 10,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#fff",
                                    backgroundColor: profitColor,
                                    textAlign: "center",
                                    minWidth: 90,
                                  }}
                                >
                                  Profit: {formatNumber2(profitValue)}
                                </Box>
                              </Box>
                            );
                          })
                        )}
                      </Box>

                      <Box
                        flex={1}
                        borderLeft={1}
                        borderColor="divider"
                        pl={3}
                        display="flex"
                        flexDirection="column"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                      >
                        <Box display="flex" flexDirection="column" gap={1.5} width="100%">
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              width: "92%",
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total Items"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                              {totalItems}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: 1.5,
                              width: "92%",
                            }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: "10px",
                                backgroundColor: "#f5f5f5",
                                border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                                textAlign: "center",
                                minHeight: 90,
                              }}
                            >
                              <CustomLabel
                                text="Total Sold Qty"
                                fontWeight={600}
                                fontSize="0.9rem"
                                center
                              />
                              <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                                {formatNumber2(totalSoldQty)}
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                p: 2,
                                borderRadius: "10px",
                                backgroundColor: "#f5f5f5",
                                border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                                textAlign: "center",
                                minHeight: 90,
                              }}
                            >
                              <CustomLabel
                                text="Total Bought Qty"
                                fontWeight={600}
                                fontSize="0.9rem"
                                center
                              />
                              <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                                {formatNumber2(totalBoughtQty)}
                              </Box>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "repeat(2, 1fr)",
                              gap: 1.5,
                              width: "92%",
                            }}
                          >
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: "10px",
                                backgroundColor: "#f5f5f5",
                                border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                                textAlign: "center",
                                minHeight: 90,
                              }}
                            >
                              <CustomLabel
                                text="Total Sold Amount"
                                fontWeight={600}
                                fontSize="0.9rem"
                                center
                              />
                              <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                                {formatNumber2(totalSoldAmount)}
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                p: 2,
                                borderRadius: "10px",
                                backgroundColor: "#f5f5f5",
                                border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                                textAlign: "center",
                                minHeight: 90,
                              }}
                            >
                              <CustomLabel
                                text="Total Bought Amount"
                                fontWeight={600}
                                fontSize="0.9rem"
                                center
                              />
                              <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                                {formatNumber2(totalBoughtAmount)}
                              </Box>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              width: "92%",
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total Cost"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                              {formatNumber2(totalCost)}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              width: "92%",
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total Profit"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box
                              sx={{
                                fontWeight: 700,
                                fontSize: "1.1rem",
                                mt: 0.5,
                                color: totalProfit < 0 ? "#ef4444" : "#16a34a",
                              }}
                            >
                              {formatNumber2(totalProfit)}
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </Box>
          )}

          {tabIndex === 2 && (
            <Box sx={{ display: "grid", gap: 2 }}>
              {reportsError ? (
                <Typography color="error">{reportsError}</Typography>
              ) : null}
              {(() => {
                const items = Array.isArray(transactionsResponse?.content)
                  ? transactionsResponse.content
                  : Array.isArray(transactionsResponse?.data)
                  ? transactionsResponse.data
                  : Array.isArray(transactionsResponse)
                  ? transactionsResponse
                  : [];

                const txSearch = transactionsSearch.trim().toLowerCase();
                const filteredItems = txSearch
                  ? items.filter((i: any) => {
                      const product = String(i?.productName || "").toLowerCase();
                      const sourceType = String(i?.sourceType || "").toLowerCase();
                      const sourceId = String(i?.sourceId || "").toLowerCase();
                      return (
                        product.includes(txSearch) ||
                        sourceType.includes(txSearch) ||
                        sourceId.includes(txSearch)
                      );
                    })
                  : items;

                const totalFromResponse =
                  transactionsResponse?.totalElements ??
                  transactionsResponse?.total ??
                  transactionsResponse?.data?.totalElements ??
                  transactionsResponse?.data?.total;
                const totalTransactions =
                  txSearch ? filteredItems.length : totalFromResponse ?? items.length;

                const totals = filteredItems.reduce(
                  (acc: any, i: any) => {
                    const dir = String(i?.direction || "").toUpperCase();
                    const qty = Number(i?.qty) || 0;
                    const amount = Number(i?.totalPrice) || 0;
                    if (dir === "IN") {
                      acc.inQty += qty;
                      acc.inAmount += amount;
                    } else if (dir === "OUT") {
                      acc.outQty += qty;
                      acc.outAmount += amount;
                    }
                    return acc;
                  },
                  { inQty: 0, outQty: 0, inAmount: 0, outAmount: 0 }
                );
                const netAmount = totals.outAmount - totals.inAmount;

                return (
                  <Box display="flex" gap={3}>
                    <Box flex={2}>
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                        mb={1}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          Transactions
                        </Typography>

                        <Box
                          sx={{ width: { xs: "100%", sm: 160, md: 180 }, maxWidth: 180 }}
                        >
                          <CustomSelect
                            options={[
                              { label: "All", value: "all" },
                              ...businesses.map((b) => ({
                                label: b.name,
                                value: String(b.id),
                              })),
                            ]}
                            value={transactionsBusinessId}
                            onChange={(v: any) => {
                              setTransactionsBusinessId(String(v));
                              setTransactionsPage(1);
                            }}
                            placeholder="Business"
                            size="medium"
                            searchable={false}
                            multiSelect={false}
                            sx={{
                              width:'180px',
                              backgroundColor: COLORS.WhiteBackground,
                              "& .MuiSelect-select": {
                                padding: "8px 12px",
                              },
                            }}
                          />
                        </Box>

                        <Box sx={{ width: { xs: "100%", sm: 180 }, maxWidth: 200 }}>
                          <CustomTextField
                            size="small"
                            startIcon={<SearchIcon />}
                            placeholder="Search product"
                            bgColor={COLORS.WhiteBackground}
                            value={transactionsSearch}
                            onChange={(e) => {
                              setTransactionsSearch(e.target.value);
                              setTransactionsPage(1);
                            }}
                            sx={{ width: "100%" }}
                          />
                        </Box>
                      </Box>

                      <Box
                        mb={1}
                        display="flex"
                        gap={1}
                        flexWrap="wrap"
                        alignItems="flex-end"
                      >
                        <Box minWidth={90}>
                          <CustomLabel text="From" variant="body1" gutterBottom />
                          <CustomDateField
                            sx={{ width: "120px" }}
                            value={transactionsFrom || ""}
                            onChange={(value) => {
                              setTransactionsFrom(value || null);
                              setTransactionsPage(1);
                            }}
                            fullWidth
                          />
                        </Box>

                        <Box minWidth={90}>
                          <CustomLabel text="To" variant="body1" gutterBottom />
                          <CustomDateField
                            sx={{ width: "120px" }}
                            value={transactionsTo || ""}
                            onChange={(value) => {
                              setTransactionsTo(value || null);
                              setTransactionsPage(1);
                            }}
                            fullWidth
                          />
                        </Box>

                        <Box display="flex" alignItems="flex-end" gap={1} mb={0.5}>
                          <Typography
                            sx={{ fontSize: "0.875rem", color: "#9ca3af", mb: "6px" }}
                          >
                            Rows per page:
                          </Typography>
                          <Select
                            value={transactionsSize}
                            onChange={(e) => {
                              setTransactionsSize(Number(e.target.value));
                              setTransactionsPage(1);
                            }}
                            sx={{
                              height: "32px",
                              fontSize: "0.875rem",
                              width: "80px",
                            }}
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </Box>

                        <Box display="flex" alignItems="flex-end" gap={0.5} mb={0.5}>
                          {(() => {
                            const items = Array.isArray(transactionsResponse?.content)
                              ? transactionsResponse.content
                              : Array.isArray(transactionsResponse?.data)
                              ? transactionsResponse.data
                              : Array.isArray(transactionsResponse)
                              ? transactionsResponse
                              : [];
                            const totalItems =
                              transactionsResponse?.totalElements ??
                              transactionsResponse?.total ??
                              transactionsResponse?.data?.totalElements ??
                              transactionsResponse?.data?.total ??
                              items.length;
                            const totalPages = Math.max(
                              1,
                              Math.ceil(Number(totalItems || 0) / transactionsSize)
                            );
                            return (
                              <>
                                <IconButton
                                  disabled={transactionsPage <= 1}
                                  onClick={() =>
                                    setTransactionsPage((p) =>
                                      Math.max(p - 1, 1)
                                    )
                                  }
                                  sx={{
                                    color: transactionsPage > 1 ? "#9ca3af" : "#ccc",
                                    p: 0.5,
                                  }}
                                >
                                  <ArrowBackIosNew fontSize="small" />
                                </IconButton>

                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: "bold",
                                    color: "#9ca3af",
                                    alignSelf: "center",
                                  }}
                                >
                                  {transactionsPage} / {totalPages}
                                </Typography>

                                <IconButton
                                  disabled={transactionsPage >= totalPages}
                                  onClick={() =>
                                    setTransactionsPage((p) =>
                                      Math.min(p + 1, totalPages)
                                    )
                                  }
                                  sx={{
                                    color: transactionsPage < totalPages ? "#9ca3af" : "#ccc",
                                    p: 0.5,
                                  }}
                                >
                                  <ArrowForwardIos fontSize="small" />
                                </IconButton>
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                      {reportsLoading ? (
                        <Box display="flex" justifyContent="center" py={2}>
                          <CircularProgress />
                        </Box>
                      ) : filteredItems.length === 0 ? (
                        <CustomLabel text="No transactions found." />
                      ) : (
                        filteredItems.map((item: any, idx: number) => {
                          const direction = String(item?.direction || "").toUpperCase();
                          const directionColor =
                            direction === "OUT"
                              ? "#16a34a"
                              : direction === "IN"
                              ? "#ef4444"
                              : "#6b7280";
                          const dateLabel = formatDateDMY(item?.sourceDate);
                          return (
                            <Box
                              key={item?.id || idx}
                              sx={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 2,
                                p: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 2,
                                mb: 1.5,
                              }}
                            >
                              <Box>
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                      xs: "1fr",
                                      sm: "180px 1fr",
                                    },
                                    alignItems: "center",
                                    columnGap: 2,
                                    rowGap: 0.5,
                                    minHeight: 28,
                                  }}
                                >
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CustomLabel
                                      text={`${item?.productName || "Product"}`}
                                      fontWeight={700}
                                      fontSize={18}
                                    />
                                    {direction === "OUT" ? (
                                      <ArrowUpwardIcon
                                        fontSize="small"
                                        sx={{ color: "#16a34a" }}
                                      />
                                    ) : direction === "IN" ? (
                                      <ArrowDownwardIcon
                                        fontSize="small"
                                        sx={{ color: "#ef4444" }}
                                      />
                                    ) : null}
                                  </Box>
                                  {(() => {
                                    const sourceType = String(
                                      item?.sourceType || "—"
                                    );
                                    const sourceTypeKey = sourceType
                                      .toUpperCase()
                                      .replace(/\s+/g, "_");
                                    const showSourceId =
                                      sourceTypeKey !== "STOCK_TRANSFER" &&
                                      item?.sourceId;
                                    return (
                                      <Box
                                    sx={{
                                      width: { xs: "100%", sm: 220 },
                                      maxWidth: 220,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    <CustomLabel
                                      text={`${sourceType}${
                                        showSourceId ? ` • #${item.sourceId}` : ""
                                      }`}
                                      fontWeight={700}
                                      fontSize={13}
                                      color="#374151"
                                    />
                                  </Box>
                                    );
                                  })()}
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 6,
                                    mt: 0.5,
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <Box sx={{ display: "grid", rowGap: 0.5, minWidth: 160 }}>
                                    <CustomLabel
                                      text={`Qty: ${item?.qty ?? "—"}`}
                                      fontSize={15}
                                      fontWeight={700}
                                      color="#374151"
                                    />
                                    <CustomLabel
                                      text={`Unit Price: ${
                                        item?.unitPrice ?? item?.unitPrice === 0
                                          ? item.unitPrice
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                    <CustomLabel
                                      text={`Total Price: ${
                                        item?.totalPrice ?? item?.totalPrice === 0
                                          ? item.totalPrice
                                          : "—"
                                      }`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                  </Box>
                                  <Box sx={{ display: "grid", rowGap: 0.5, minWidth: 160 }}>
                                    <Box sx={{ height: 22 }} />
                                    <CustomLabel
                                      text={`Date: ${dateLabel}`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                    <CustomLabel
                                      text={`Business: ${item?.businessName || "—"}`}
                                      fontSize={14}
                                      color="#6b7280"
                                    />
                                  </Box>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 10,
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#fff",
                                  backgroundColor: directionColor,
                                  textAlign: "center",
                                  minWidth: 90,
                                }}
                              >
                                {direction || "—"}
                              </Box>
                            </Box>
                          );
                        })
                      )}
                    </Box>

                    <Box
                      flex={1}
                      borderLeft={1}
                      borderColor="divider"
                      pl={3}
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                    >
                      <Box display="flex" flexDirection="column" gap={1.5} width="100%">
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            backgroundColor: "#f5f5f5",
                            border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                            width: "92%",
                            textAlign: "center",
                            minHeight: 90,
                          }}
                        >
                          <CustomLabel
                            text="Total Transactions"
                            fontWeight={600}
                            fontSize="0.9rem"
                            center
                          />
                          <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                            {totalTransactions}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 1.5,
                            width: "92%",
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total IN Qty"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                              {totals.inQty}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total OUT Qty"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                              {totals.outQty}
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(2, 1fr)",
                            gap: 1.5,
                            width: "92%",
                          }}
                        >
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total IN Amount"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                              {totals.inAmount.toFixed(2)}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              p: 2,
                              borderRadius: "10px",
                              backgroundColor: "#f5f5f5",
                              border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                              textAlign: "center",
                              minHeight: 90,
                            }}
                          >
                            <CustomLabel
                              text="Total OUT Amount"
                              fontWeight={600}
                              fontSize="0.9rem"
                              center
                            />
                            <Box sx={{ fontWeight: 700, fontSize: "1.1rem", mt: 0.5 }}>
                              {totals.outAmount.toFixed(2)}
                            </Box>
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            p: 2,
                            borderRadius: "10px",
                            backgroundColor: "#f5f5f5",
                            border: `1px solid ${COLORS.BorderColor || "#e0e0e0"}`,
                            width: "92%",
                            textAlign: "center",
                            minHeight: 90,
                          }}
                        >
                          <CustomLabel
                            text="Net Amount"
                            fontWeight={600}
                            fontSize="0.9rem"
                            center
                          />
                          <Box
                            sx={{
                              fontWeight: 700,
                              fontSize: "1.1rem",
                              mt: 0.5,
                              color: netAmount < 0 ? "#ef4444" : "#16a34a",
                            }}
                          >
                            {netAmount.toFixed(2)}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })()}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Products;
